import React, { useState, useEffect } from "react";
import {
  styled,
  useTheme,
  Avatar,
  Badge,
  Box,
  Typography,
  IconButton,
  TextField,
  Tooltip,
  CircularProgress,
  Divider,
  InputAdornment,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import ImageIcon from "@mui/icons-material/Image";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import VideoFileIcon from "@mui/icons-material/VideoFile";
import AudioFileIcon from "@mui/icons-material/AudioFile";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import ExitToAppOutlinedIcon from "@mui/icons-material/ExitToAppOutlined";
import "../../styles/UserList.css";
import {
  addUser,
  removeUser,
  addAgent,
  removeAgent,
  getFileURL,
} from "../../resources/api.resource.ts";
import ConfirmDialog from "./ConfirmDialog.tsx";
import { MemberOption } from "../../models/User.ts";
import store from "../../utils/store.ts";
import { getSenderColor } from "../../utils/senderColor.ts";

interface UserListV2Props {
  pinned?: boolean;
  onAction: (action: string) => void;
  onRemoved?: (memberName: string, undoFn: () => Promise<void>) => void;
  onAdded?: (memberName: string) => void;
  groupId: string;
  groupName?: string;
  collaborationId: string;
  drawerType: string;
  list?: any[];
  listLoading?: boolean;
  refreshSidebar: () => void | undefined;
  showAddRemove: boolean;
  refreshRightPanel: () => any[];
  uId: string;
}

const StyledBadge = styled(Badge)(() => ({
  "& .MuiBadge-badge": {
    backgroundColor: "#44b700",
    color: "#44b700",
    boxShadow: "0 0 0 2px transparent",
    width: "10px",
    height: "10px",
    borderRadius: "50%",
    minWidth: "10px",
  },
}));

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0]?.toUpperCase() ?? "?";
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const FILE_TYPE_STYLES: Record<
  string,
  { bg: string; fg: string; icon: React.ReactNode }
> = {
  pdf:     { bg: "#EF4444", fg: "#fff", icon: <PictureAsPdfIcon fontSize="small" /> },
  image:   { bg: "#10B981", fg: "#fff", icon: <ImageIcon fontSize="small" /> },
  video:   { bg: "#8B5CF6", fg: "#fff", icon: <VideoFileIcon fontSize="small" /> },
  audio:   { bg: "#F59E0B", fg: "#fff", icon: <AudioFileIcon fontSize="small" /> },
  doc:     { bg: "#3B82F6", fg: "#fff", icon: <DescriptionIcon fontSize="small" /> },
  generic: { bg: "#64748B", fg: "#fff", icon: <InsertDriveFileIcon fontSize="small" /> },
};

function getFileStyle(fileType?: string) {
  if (!fileType) return FILE_TYPE_STYLES.generic;
  const t = fileType.toLowerCase();
  if (t === "application/pdf") return FILE_TYPE_STYLES.pdf;
  if (t.startsWith("image/")) return FILE_TYPE_STYLES.image;
  if (t.startsWith("video/")) return FILE_TYPE_STYLES.video;
  if (t.startsWith("audio/")) return FILE_TYPE_STYLES.audio;
  if (t.includes("document") || t.includes("text") || t.includes("word") || t.includes("sheet"))
    return FILE_TYPE_STYLES.doc;
  return FILE_TYPE_STYLES.generic;
}

const UserListV2: React.FC<UserListV2Props> = ({
  onAction,
  onRemoved,
  onAdded,
  groupId,
  groupName,
  collaborationId,
  drawerType,
  list,
  listLoading,
  refreshSidebar,
  showAddRemove,
  uId,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const surfacePalette = (theme.palette as any).surface ?? {};
  const primaryText   = surfacePalette.primaryText   ?? (isDark ? '#FFFFFF'                 : '#1A1F2E');
  const secondaryText = surfacePalette.secondaryText ?? (isDark ? 'rgba(255,255,255,0.65)' : '#7A8194');
  const panelBg       = surfacePalette.chatPanelBg   ?? (isDark ? '#3B4053'                : '#ffffff');
  const dividerColor  = surfacePalette.divider       ?? (isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)');
  const searchBg      = isDark ? 'rgba(0,0,0,0.2)'   : '#F7F8FA';
  const itemHoverBg   = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)';
  const actionHoverBg = isDark ? 'rgba(128,128,128,0.08)' : 'rgba(0,0,0,0.04)';

  const title = drawerType === "users" ? "GROUP MEMBERS" : "SHARED FILES";
  const [drawerList, setDrawerList] = useState<any[]>(list ?? []);
  const [searchQuery, setSearchQuery] = useState("");
  const [confirmation, setConfirmation] = useState(false);
  const [selectedMember, setSelectedMember] = useState<any | null>(null);
  const [addingMembers, setAddingMembers] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setDrawerList(list ?? []);
  }, [list]);

  const filteredList = drawerList.filter((item) =>
    (item.name ?? "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleDownload = async (file: any) => {
    if (!file.url) return;
    setDownloadingId(file.id);
    try {
      const objFile = {
        collaboration_id: collaborationId,
        collaboration_instance_id: groupId,
        file_name: file.url.split("/").pop(),
      };
      const resp = await getFileURL(objFile);
      window.open(resp?.url ?? file.url, "_blank");
    } catch {
      window.open(file.url, "_blank");
    } finally {
      setDownloadingId(null);
    }
  };

  const openRemove = (person: any) => {
    setSelectedMember(person);
    setConfirmation(true);
  };

  const confirmRemove = async () => {
    if (!selectedMember) return;
    setIsLoading(true);
    try {
      const allUsers = store.getState().allUsers;
      const memberData = allUsers.find((u: any) => u.uid === selectedMember.id);
      const isAgent = memberData?.userType === "agent";

      const resp = isAgent
        ? await removeAgent(collaborationId, selectedMember.id)
        : await removeUser({ collaboration_id: collaborationId, user_uid: selectedMember.id });

      if (resp) {
        setConfirmation(false);
        const removed = selectedMember;
        setDrawerList((prev) => prev.filter((item) => item.id !== removed.id));
        refreshSidebar();

        const undoFn = async () => {
          const isAgentUndo = memberData?.userType === "agent";
          if (isAgentUndo) {
            await addAgent(collaborationId, removed.id);
          } else {
            await addUser({ collaboration_id: collaborationId, memberUids: [removed.id] });
          }
          setDrawerList((prev) => [...prev, removed]);
          refreshSidebar();
        };

        onRemoved?.(removed.name, undoFn);
        setSelectedMember(null);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddSingle = async (member: MemberOption) => {
    setIsLoading(true);
    try {
      if (member.userType === "agent") {
        await addAgent(collaborationId, member.uid);
      } else {
        await addUser({ collaboration_id: collaborationId, memberUids: [member.uid] });
      }
      const newEntry = { ...member, id: member.uid };
      setDrawerList((prev) => [...prev, newEntry]);
      refreshSidebar();
      onAdded?.(member.name);
      setAddingMembers(false);
      setSearchQuery("");
    } catch (error) {
      console.error("Failed to add member:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const confirmTitle = selectedMember
    ? `Remove ${selectedMember.name} from the "${groupName ?? "Group"}" Group?`
    : "";

  const memberIds = new Set(drawerList.map((m) => m.id));
  const allUsersForAdd: MemberOption[] = store.getState().allUsers ?? [];
  const addSearchResults = allUsersForAdd.filter(
    (u) =>
      u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (u.email ?? "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Box role="presentation" className="user-list-v2">
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "16px 20px",
          borderBottom: `1px solid ${dividerColor}`,
          backgroundColor: panelBg,
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{
            fontSize: "11px",
            fontWeight: 700,
            color: secondaryText,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
          }}
        >
          {title}
        </Typography>
        <Tooltip title="Close panel">
          <IconButton
            size="small"
            onClick={() => onAction("close")}
            sx={{ "&:hover": { backgroundColor: actionHoverBg } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Search */}
      <Box sx={{ padding: "12px 16px", flexShrink: 0 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Search..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: secondaryText }} />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              backgroundColor: searchBg,
              "& fieldset": { border: "none" },
            },
            "& input": { fontSize: "13px", color: primaryText },
          }}
        />
      </Box>

      {/* List */}
      <Box sx={{ flex: 1, overflowY: "auto" }}>
        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", padding: "16px" }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {/* Add-member search mode */}
        {!isLoading && addingMembers && (
          <>
            {searchQuery.length === 0 && (
              <Box sx={{ padding: "24px 16px", textAlign: "center" }}>
                <Typography variant="body2" sx={{ fontSize: "13px", color: "#7A8194" }}>
                  Type a name or email to search
                </Typography>
              </Box>
            )}
            {searchQuery.length > 0 && addSearchResults.length === 0 && (
              <Box sx={{ padding: "24px 16px", textAlign: "center" }}>
                <Typography variant="body2" sx={{ fontSize: "13px", color: "#7A8194" }}>
                  No users found
                </Typography>
              </Box>
            )}
            {searchQuery.length > 0 && addSearchResults.map((user, index) => {
              const isMember = memberIds.has(user.uid);
              const color = getSenderColor(user.uid);
              return (
                <React.Fragment key={user.uid}>
                  {index > 0 && <Divider sx={{ mx: 2 }} />}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      padding: "10px 16px",
                      transition: "background-color 0.15s",
                      "&:hover": { backgroundColor: "rgba(59, 130, 246, 0.06)" },
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 36,
                        height: 36,
                        bgcolor: color.avatarBg,
                        color: color.avatarFg,
                        fontSize: "13px",
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      {getInitials(user.name)}
                    </Avatar>
                    <Box sx={{ flex: 1, overflow: "hidden" }}>
                      <Typography
                        sx={{
                          fontSize: "13px",
                          fontWeight: 600,
                          color: primaryText,
                          lineHeight: 1.3,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {user.name}
                      </Typography>
                      {user.email && (
                        <Typography
                          sx={{
                            fontSize: "11px",
                            color: secondaryText,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {user.email}
                        </Typography>
                      )}
                    </Box>
                    {!isMember && (
                      <Button
                        size="small"
                        startIcon={<PersonAddOutlinedIcon sx={{ fontSize: "14px !important" }} />}
                        onClick={() => handleAddSingle(user)}
                        sx={{
                          color: isDark ? primaryText : "#0857c3",
                          fontSize: "12px",
                          fontWeight: 600,
                          textTransform: "none",
                          padding: "2px 8px",
                          minWidth: 0,
                          flexShrink: 0,
                          "&:hover": { backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(8,87,195,0.08)" },
                        }}
                      >
                        Add Member
                      </Button>
                    )}
                  </Box>
                </React.Fragment>
              );
            })}
          </>
        )}

        {/* Normal members / files list */}
        {!isLoading && !addingMembers && (
          <>
            {!listLoading && filteredList.length === 0 && (
              <Box sx={{ padding: "24px 16px", textAlign: "center", color: "text.secondary" }}>
                <Typography variant="body2" sx={{ fontSize: "13px" }}>
                  {drawerType === "users" ? "No members yet" : "No attachments yet"}
                </Typography>
              </Box>
            )}
            {listLoading && (
              <Box sx={{ display: "flex", justifyContent: "center", padding: "16px" }}>
                <CircularProgress size={24} />
              </Box>
            )}

            {drawerType === "users"
              ? filteredList.map((item, index) => (
                  <React.Fragment key={item.id}>
                    {index > 0 && <Divider sx={{ mx: 2 }} />}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        padding: "10px 16px",
                        cursor: "default",
                        transition: "background-color 0.15s",
                        "&:hover": { backgroundColor: "rgba(59, 130, 246, 0.06)" },
                        "&:hover .remove-btn": { opacity: 1 },
                      }}
                    >
                      {(() => {
                        const color = getSenderColor(item.id);
                        return (
                          <StyledBadge
                            overlap="circular"
                            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                            variant={item.isOnline ? "dot" : "standard"}
                            invisible={!item.isOnline}
                          >
                            <Avatar
                              sx={{
                                width: 36,
                                height: 36,
                                bgcolor: color.avatarBg,
                                color: color.avatarFg,
                                fontSize: "13px",
                                fontWeight: 700,
                                flexShrink: 0,
                              }}
                            >
                              {getInitials(item.name)}
                            </Avatar>
                          </StyledBadge>
                        );
                      })()}
                      <Box sx={{ flex: 1, overflow: "hidden" }}>
                        <Typography
                          sx={{
                            fontSize: "13px",
                            fontWeight: 600,
                            color: primaryText,
                            lineHeight: 1.3,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item.name}
                        </Typography>
                        {item.email && (
                          <Typography
                            sx={{
                              fontSize: "11px",
                              color: secondaryText,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {item.email}
                          </Typography>
                        )}
                      </Box>
                      {showAddRemove && item.id !== uId && (
                        <Button
                          className="remove-btn"
                          size="small"
                          startIcon={<CloseIcon sx={{ fontSize: "14px !important" }} />}
                          onClick={() => openRemove(item)}
                          disabled={isLoading}
                          sx={{
                            opacity: 0,
                            transition: "opacity 0.15s",
                            color: "error.main",
                            fontSize: "12px",
                            fontWeight: 600,
                            textTransform: "none",
                            padding: "2px 8px",
                            minWidth: 0,
                            "&:hover": { backgroundColor: "rgba(211,47,47,0.08)" },
                          }}
                        >
                          Remove
                        </Button>
                      )}
                    </Box>
                  </React.Fragment>
                ))
              : filteredList.map((item) => (
                  <Box
                    key={item.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      padding: "10px 16px",
                      transition: "background-color 0.15s",
                      "&:hover": { backgroundColor: itemHoverBg },
                    }}
                  >
                    {(() => {
                      const style = getFileStyle(item.fileType);
                      return (
                        <Avatar
                          variant="rounded"
                          sx={{
                            width: 36,
                            height: 36,
                            bgcolor: style.bg,
                            color: style.fg,
                            borderRadius: "8px",
                            flexShrink: 0,
                          }}
                        >
                          {style.icon}
                        </Avatar>
                      );
                    })()}
                    <Box sx={{ flex: 1, overflow: "hidden" }}>
                      <Typography
                        sx={{
                          fontSize: "13px",
                          fontWeight: 600,
                          color: primaryText,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {item.name}
                      </Typography>
                      {item.uploadedBy && (
                        <Typography sx={{ fontSize: "11px", color: "#7A8194" }}>
                          {item.uploadedBy}
                        </Typography>
                      )}
                    </Box>
                    <Tooltip title={downloadingId === item.id ? "Downloading…" : "Download"}>
                      <span>
                        <IconButton
                          onClick={() => handleDownload(item)}
                          disabled={downloadingId === item.id}
                          size="small"
                          sx={{
                            color: secondaryText,
                            "&:hover": { color: primaryText, backgroundColor: actionHoverBg },
                          }}
                        >
                          {downloadingId === item.id ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <DownloadIcon sx={{ fontSize: 18 }} />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                ))}
          </>
        )}
      </Box>

      {/* Bottom actions — members panel only */}
      {drawerType === "users" && showAddRemove && (
        <>
          <Divider />
          <Box sx={{ padding: "8px 4px 12px" }}>
            <Button
              startIcon={<PersonAddOutlinedIcon sx={{ fontSize: 18 }} />}
              onClick={() => {
                setAddingMembers((v) => !v);
                setSearchQuery("");
              }}
              sx={{
                width: "100%",
                justifyContent: "flex-start",
                padding: "8px 16px",
                fontSize: "13px",
                fontWeight: 500,
                color: isDark ? primaryText : (addingMembers ? "#0857c3" : primaryText),
                textTransform: "none",
                borderRadius: 0,
                "&:hover": { backgroundColor: actionHoverBg },
              }}
            >
              {addingMembers ? "← Back to Members" : "Add Member"}
            </Button>
            {!addingMembers && (
              <Button
                startIcon={<ExitToAppOutlinedIcon sx={{ fontSize: 18 }} />}
                onClick={() => onAction("leave")}
                sx={{
                  width: "100%",
                  justifyContent: "flex-start",
                  padding: "8px 16px",
                  fontSize: "13px",
                  fontWeight: 500,
                  color: "#E11D48",
                  textTransform: "none",
                  borderRadius: 0,
                  "&:hover": { backgroundColor: "rgba(225,29,72,0.06)" },
                }}
              >
                Leave Group
              </Button>
            )}
          </Box>
        </>
      )}

      <ConfirmDialog
        color="error"
        confirmLabel="Remove"
        open={confirmation}
        title={confirmTitle}
        message="He'll lose access to group information, but you can add him again anytime."
        onConfirm={confirmRemove}
        onClose={() => { setConfirmation(false); setSelectedMember(null); }}
      />
    </Box>
  );
};

export default UserListV2;
